import { Fragment } from 'react';

import Box from '~/common/components/Box';
import Text from '~/common/components/Texts/Text';
import TitleCaseUi from '~/common/components/TitleCaseUi';
import { formatCurrency } from '~/common/util/format';
import type { WvoeCalculationResult } from '~/pages/IncomeCalculator/types';

import { PaySchedule } from '../../../enums';
import { RecentRaiseSection } from '../RecentRaise';
import {
  FHA_24_MONTH_CALCULATION_HEADER,
  FHA_QUALIFYING_INCREASING_TREND,
} from '../constants';
import AccordionWrapper from './AccordionWrapper';
import WvoeBreakdown from './WvoeBreakdown';
import WvoeSection from './WvoeSection';
import WvoeTrendingSection from './WvoeTrendingSection';

const TitleBox = TitleCaseUi(Box);

const copyTrend = {
  Undefined: { copy: 'Undefined' },
  Classic: { copy: '' },
  IncreasingTrend: { copy: ' - Increasing' },
  DecreasingTrend: { copy: ' - Declining' },
  DecliningButStabilizingTrendAboveAverage: {
    copy: ' - Declining but Stabilized',
  },
  DecliningButStabilizingTrendBelowAverage: {
    copy: ' - Declining but Stabilized',
  },
};

type CalculationBreakdownProps = {
  selectedResult: WvoeCalculationResult;
  hideAccordion?: boolean;
  fullWidth?: boolean;
};

type AnnualDetail =
  WvoeCalculationResult['annualDetails'][number]['calculationResults'][number];

const CalculationBreakdown = ({
  selectedResult,
  hideAccordion = false,
  fullWidth = false,
}: Readonly<CalculationBreakdownProps>) => {
  const annualDetails = selectedResult?.annualDetails || [];
  const {
    calculationResults,
    currentGrossBasePay,
    payType,
    ytdMonthlyAverageEquation,
    monthsWorkedThisYear,
  } = annualDetails[0] || {
    calculationResults: [],
    currentGrossBasePay: 0,
    payType: 0,
    ytdMonthlyAverageEquation: '',
    monthsWorkedThisYear: 0,
  };
  const throughDate = selectedResult?.throughDate || '';

  const containsRecentRaise = (result: AnnualDetail, payType: number) =>
    result.incomeType === 'Base' &&
    payType === PaySchedule['Variable Hourly'] &&
    result?.additionalCalculationDetails;

  const getIncomeTypeTitle = (result: AnnualDetail, payType: number) => {
    if (containsRecentRaise(result, payType)) {
      return `${result.incomeType} - Recent Raise`;
    }
    return result.incomeType;
  };

  const getTitleSuffix = (result: AnnualDetail, payType: number) => {
    if (containsRecentRaise(result, payType)) return '';
    const fhaSuffix =
      result.formulaType === FHA_QUALIFYING_INCREASING_TREND
        ? FHA_24_MONTH_CALCULATION_HEADER
        : '';
    return `${copyTrend[result.trend].copy}${fhaSuffix}`;
  };

  return (
    <AccordionWrapper allowMultiple hide={hideAccordion}>
      {calculationResults
        ?.filter((result) => result.trend !== 'Undefined')
        .map((result) => (
          <Fragment key={result.incomeType}>
            <>
              <TitleBox>
                <span style={{ fontWeight: 500 }}>
                  {getIncomeTypeTitle(result, payType)}
                </span>
                {getTitleSuffix(result, payType)}
              </TitleBox>
              <Text flex="1" textAlign="right" fontWeight={500} mr="18.5">
                {formatCurrency(result.calculatedMonthlyIncome)}
              </Text>
            </>

            {getSectionComponent(
              result,
              payType,
              ytdMonthlyAverageEquation,
              currentGrossBasePay,
              throughDate,
              monthsWorkedThisYear,
              fullWidth,
            )}
          </Fragment>
        ))}
    </AccordionWrapper>
  );
};

const getSectionComponent = (
  result: AnnualDetail,
  payType: number,
  ytdMonthlyAverageEquation: string,
  currentGrossBasePay: number,
  throughDate: string,
  monthsWorkedThisYear: number,
  fullWidth: boolean,
) => {
  if (
    result.incomeType === 'Base' &&
    payType === PaySchedule['Variable Hourly'] &&
    result?.additionalCalculationDetails
  ) {
    return (
      <RecentRaiseSection
        additionalCalculationDetails={result.additionalCalculationDetails}
        fullWidth={fullWidth}
      />
    );
  }

  if (result.formulaType === FHA_QUALIFYING_INCREASING_TREND) {
    return (
      <WvoeTrendingSection
        key={result.incomeType}
        throughDate={throughDate}
        monthsWorkedThisYear={monthsWorkedThisYear}
        {...result}
      />
    );
  }
  if (
    result.incomeType === 'Base' &&
    payType !== PaySchedule['Variable Hourly']
  ) {
    return (
      <WvoeBreakdown
        result={result}
        payType={payType}
        ytdMonthlyAverageEquation={ytdMonthlyAverageEquation}
        currentGrossBasePay={currentGrossBasePay}
      />
    );
  }
  return <WvoeSection key={result.incomeType} {...result} />;
};

CalculationBreakdown.defaulProps = {};

export default CalculationBreakdown;
